// Order a Test, Part 4 — renders a requisition schema (fetched, as of the
// 2026-10-09 security fix, through this app's own clinician-authenticated
// GET /clinician/order-requisition-schema/{schema_key} proxy, which calls
// labariumaireport's GET /api/order-requisition/schema/{schema_key}
// server-side — see OrderTest.js) generically, the same "one source of
// truth, never hardcode the field
// list a second time" discipline requisition_schema.py already uses for
// the Requisition Conversion tool's own web form. Field types match
// order_requisition_schema.py exactly: text, date, number, single_select,
// checkbox_group (supports exclusive_code), multi_select, radio_row, table
// (both the clinician-added-rows shape and the schema-fixed-rows shape,
// e.g. Toxicology's "poc_results" — see FixedRowsTableField below).
import React from "react";
import {
  Box, Typography, TextField, Checkbox, FormControlLabel, Radio,
  RadioGroup, Autocomplete, Chip, IconButton, Tooltip, Table, TableBody,
  TableCell, TableHead, TableRow, Button,
} from "@mui/material";
import { InfoOutlined, Delete, Add } from "@mui/icons-material";

const BRAND = { navy: "#00357a", gold: "#c9a96e" };

// 2026-10-13 fix — real gap confirmed in today's UI/UX sweep: FieldLabel's
// gold asterisk was purely decorative — nothing anywhere in the frontend
// ever actually checked whether a `required: true` field was filled in
// before letting a clinician proceed past Order a Test's stepper, so a
// clinician could click through every step with required fields empty and
// only find out via a generic server-side 422 at the very last step.
// `missing` (new, additive prop — defaults to false so every existing
// caller that doesn't pass it renders exactly as before) now drives a red
// label + a real "Required" helper line, not just a static gold asterisk.
function FieldLabel({ label, help, required, isDark, missing }) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: missing ? "#dc2626" : (isDark ? "#cbd5e1" : "#334155") }}>
          {label}
          {required && <Box component="span" sx={{ color: missing ? "#dc2626" : BRAND.gold, ml: 0.4 }}>*</Box>}
        </Typography>
        {help && (
          <Tooltip title={help} arrow placement="top">
            <InfoOutlined sx={{ fontSize: 14, color: isDark ? "#94a3b8" : "#64748b", cursor: "help" }} />
          </Tooltip>
        )}
      </Box>
      {missing && (
        <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 0.25 }}>This field is required.</Typography>
      )}
    </Box>
  );
}

// Per-type "is this required field actually empty" check — mirrors how
// each RequisitionField type stores its value (see RequisitionField/
// setField below), so a checkbox_group/multi_select/table/radio_row field
// isn't wrongly flagged just because its value isn't a plain string.
export function isFieldMissing(field, value) {
  if (!field || !field.required) return false;
  switch (field.type) {
    case "checkbox_group":
    case "multi_select":
      return !Array.isArray(value) || value.length === 0;
    case "table":
      return field.fixed_rows
        ? (!value || typeof value !== "object" || Array.isArray(value) || Object.keys(value).length === 0)
        : (!Array.isArray(value) || value.length === 0);
    case "radio_row":
      return !value || typeof value !== "object" || Object.keys(value).length === 0;
    default:
      return value === undefined || value === null || String(value).trim() === "";
  }
}

// Validates every `required: true` field across a full schema (an array
// of sections, same shape this component itself renders) against a flat
// data object, same contract OrderTest.js already uses for
// sharedClinicalData/clinicalDataByFamily[schemaKey]. Returns the missing
// fields' ids (a plain array, easy to turn into a Set at the call site)
// rather than booleans only, so the caller can both gate a Next/Submit
// button AND tell DynamicRequisitionForm which specific fields to
// highlight red via the new `errorFieldIds` prop below.
export function validateSchemaRequired(schema, data) {
  const missing = [];
  (schema || []).forEach((section) => {
    (section.fields || []).forEach((field) => {
      if (isFieldMissing(field, (data || {})[field.id])) missing.push(field.id);
    });
  });
  return missing;
}

function inputSx(isDark) {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px", fontSize: 14.5,
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,53,122,0.14)" },
    },
  };
}

// 2026-10-13 fix — real regression caught in review: every Checkbox/Radio
// in this file used `color: BRAND.navy` for BOTH its unchecked AND
// checked state, which is fine against this file's original #fff-only
// surfaces but is near-invisible (dark-navy outline on a dark-navy-ish
// #111827 surface) once a real `isDark` is actually passed in from
// OrderTest.js/MyPatients.js (previously always `false`, so this never
// mattered). Unchecked now falls back to a visible light-grey outline in
// dark mode; checked switches to the gold accent already used for every
// other interactive control in this app's dark mode (Settings.js,
// AdminClinicians.js, etc.) instead of invisible navy-on-navy.
function controlColorSx(isDark) {
  return {
    color: isDark ? "rgba(255,255,255,0.4)" : BRAND.navy,
    "&.Mui-checked": { color: isDark ? BRAND.gold : BRAND.navy },
  };
}

function BareTextField(props) {
  return <TextField hiddenLabel fullWidth size="small" {...props} />;
}

function CheckboxGroupField({ field, value, onChange, isDark, readOnly }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (code) => {
    if (readOnly) return;
    if (field.exclusive_code && code === field.exclusive_code) {
      onChange(selected.includes(code) ? [] : [code]);
      return;
    }
    let next = selected.filter((c) => c !== field.exclusive_code);
    next = next.includes(code) ? next.filter((c) => c !== code) : [...next, code];
    onChange(next);
  };
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, width: "100%" }}>
      {field.options.map((opt) => (
        <FormControlLabel
          key={opt.code}
          sx={{
            mr: 1.5,
            alignItems: "flex-start",
            flex: "1 1 280px",
            minWidth: 0,
            maxWidth: "100%",
          }}
          control={
            <Checkbox size="small" checked={selected.includes(opt.code)} onChange={() => toggle(opt.code)} disabled={readOnly}
              sx={{ ...controlColorSx(isDark), pt: 0 }} />
          }
          label={
            <Typography sx={{ fontSize: 13.5, whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "break-word", color: isDark ? "#cbd5e1" : undefined }}>
              {opt.label}
            </Typography>
          }
        />
      ))}
    </Box>
  );
}

function SingleSelectField({ field, value, onChange, isDark, readOnly }) {
  return (
    <RadioGroup row value={value || ""} onChange={(e) => onChange(e.target.value)}>
      {field.options.map((opt) => (
        <FormControlLabel key={opt.code} value={opt.code} disabled={readOnly}
          control={<Radio size="small" sx={controlColorSx(isDark)} />}
          label={<Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : undefined }}>{opt.label}</Typography>} />
      ))}
    </RadioGroup>
  );
}

function MultiSelectCodesField({ field, value, onChange, isDark, readOnly }) {
  // Open-ended — options are SUGGESTIONS only (confirmed with the user,
  // "don't make things complicated... use like multi list because there
  // can be multiple ICD codes"). freeSolo lets any code be added, not
  // just ones in the suggestion list.
  const entries = Array.isArray(value) ? value : [];
  return (
    <Autocomplete
      multiple freeSolo size="small"
      disabled={readOnly}
      options={field.options}
      getOptionLabel={(opt) => (typeof opt === "string" ? opt : `${opt.code} — ${opt.label}`)}
      value={entries}
      onChange={(e, newValue) => {
        const normalized = newValue.map((v) =>
          typeof v === "string" ? { code: v.trim(), label: "" } : v
        ).filter((v) => v.code);
        onChange(normalized);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip size="small" label={option.label ? `${option.code} — ${option.label}` : option.code}
            {...getTagProps({ index })} key={option.code + index} />
        ))
      }
      renderInput={(params) => (
        <TextField {...params} hiddenLabel placeholder="Search or type a diagnosis code" sx={inputSx(isDark)} />
      )}
    />
  );
}

function RadioRowField({ field, value, onChange, isDark, readOnly }) {
  const val = value || {};
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: "normal" }} />
            {field.options.map((o) => <TableCell key={o.value} align="center" sx={{ fontSize: 12, whiteSpace: "nowrap", color: isDark ? "#cbd5e1" : undefined }}>{o.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {field.statements.map((stmt, i) => (
            <TableRow key={i}>
              <TableCell sx={{ fontSize: 13, whiteSpace: "normal", minWidth: 220, color: isDark ? "#cbd5e1" : undefined }}>{stmt}</TableCell>
              {field.options.map((o) => (
                <TableCell key={o.value} align="center">
                  <Radio size="small" checked={val[i] === o.value} disabled={readOnly}
                    onChange={() => onChange({ ...val, [i]: o.value })}
                    sx={controlColorSx(isDark)} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function MedicationTableField({ field, value, onChange, isDark, readOnly }) {
  const rows = Array.isArray(value) && value.length ? value : [{}];
  const updateRow = (i, colId, v) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [colId]: v } : r));
    onChange(next);
  };
  const addRow = () => onChange([...rows, {}]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {field.columns.map((c) => <TableCell key={c.id} sx={{ fontSize: 12, whiteSpace: "nowrap", color: isDark ? "#cbd5e1" : undefined }}>{c.label}</TableCell>)}
              {!readOnly && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                {field.columns.map((c) => (
                  <TableCell key={c.id}>
                    <TextField hiddenLabel size="small" type={c.type === "number" ? "number" : "text"}
                      slotProps={c.type === "number" ? { htmlInput: { min: c.min, max: c.max } } : undefined}
                      value={row[c.id] || ""} onChange={(e) => updateRow(i, c.id, e.target.value)}
                      disabled={readOnly}
                      sx={{ width: c.id === "medication" ? 160 : 100, ...inputSx(isDark) }} />
                  </TableCell>
                ))}
                {!readOnly && (
                  <TableCell>
                    <IconButton size="small" onClick={() => removeRow(i)}><Delete sx={{ fontSize: 16 }} /></IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {!readOnly && (
        <Button size="small" startIcon={<Add sx={{ fontSize: 15 }} />} onClick={addRow}
          sx={{ mt: 1, textTransform: "none", fontSize: 13, color: isDark ? BRAND.gold : BRAND.navy }}>
          Add medication
        </Button>
      )}
    </Box>
  );
}

// Toxicology's POC Results table (order_requisition_schema.py's
// "poc_results" field, the only field anywhere that declares
// `fixed_rows`) — unlike MedicationTableField's one-row-per-clinician-
// added-medication shape, this table's rows are FIXED by the schema
// itself and every row always renders; the clinician only toggles each
// row's per-column checkbox. Submitted value shape MUST match what
// requisition_pdf.py's _render_fixed_rows_table reads: an object keyed
// by each fixed row's own `code`, each value a {column_id: bool} map —
// not an array of free-text rows (that's MedicationTableField's shape,
// for fields with `columns` but no `fixed_rows`).
function FixedRowsTableField({ field, value, onChange, isDark, readOnly }) {
  const rowsByCode = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  // Unchecking must DELETE the column key (and the whole row entry once
  // its last column is cleared), never just set it to `false` — a row
  // left behind as e.g. {positive: false} is still a truthy dict, which
  // would wrongly satisfy order_requisition_schema.py's "at least one
  // toxicology test option selected" validation even though nothing is
  // actually checked on this table.
  const toggle = (code, colId) => {
    const current = { ...(rowsByCode[code] || {}) };
    if (current[colId]) {
      delete current[colId];
    } else {
      current[colId] = true;
    }
    const next = { ...rowsByCode };
    if (Object.keys(current).length === 0) {
      delete next[code];
    } else {
      next[code] = current;
    }
    onChange(next);
  };
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap", color: isDark ? "#cbd5e1" : undefined }}>Medication or Drug</TableCell>
            {field.columns.map((c) => <TableCell key={c.id} align="center" sx={{ fontSize: 12, whiteSpace: "nowrap", color: isDark ? "#cbd5e1" : undefined }}>{c.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {field.fixed_rows.map((row) => (
            <TableRow key={row.code}>
              <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap", color: isDark ? "#cbd5e1" : undefined }}>{row.label}</TableCell>
              {field.columns.map((c) => (
                <TableCell key={c.id} align="center">
                  <Checkbox size="small" checked={!!(rowsByCode[row.code] && rowsByCode[row.code][c.id])}
                    onChange={() => toggle(row.code, c.id)} disabled={readOnly}
                    sx={controlColorSx(isDark)} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// `missing` adds a real red border (not just the red label above) to the
// 3 free-entry field types — the ones where a visible outline actually
// reads as "this box" (a checkbox_group/radio_row/table's own red label +
// "This field is required." line above does the same job for those
// without needing a per-option/per-cell red treatment that would be
// noisier than useful).
function missingBorderSx(missing) {
  if (!missing) return {};
  return {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#dc2626 !important", borderWidth: "1.5px" },
    },
  };
}

function RequisitionField({ field, value, onChange, isDark, readOnly, missing }) {
  switch (field.type) {
    case "text":
      return <BareTextField sx={{ ...inputSx(isDark), ...missingBorderSx(missing) }} value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly} />;
    case "date":
      return <BareTextField type="date" sx={{ ...inputSx(isDark), ...missingBorderSx(missing) }} value={value || ""} onChange={(e) => onChange(e.target.value)}
        disabled={readOnly} slotProps={{ inputLabel: { shrink: true } }} />;
    case "number":
      return <BareTextField type="number" sx={{ maxWidth: 160, ...inputSx(isDark), ...missingBorderSx(missing) }}
        slotProps={{ htmlInput: { min: field.min, max: field.max } }}
        value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readOnly}
        InputProps={field.unit ? { endAdornment: <Typography sx={{ fontSize: 13, color: "#94a3b8", ml: 0.5 }}>{field.unit}</Typography> } : undefined} />;
    case "single_select":
      return <SingleSelectField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />;
    case "checkbox_group":
      return <CheckboxGroupField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />;
    case "multi_select":
      return <MultiSelectCodesField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />;
    case "radio_row":
      return <RadioRowField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />;
    case "table":
      return field.fixed_rows
        ? <FixedRowsTableField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />
        : <MedicationTableField field={field} value={value} onChange={onChange} isDark={isDark} readOnly={readOnly} />;
    default:
      return null;
  }
}

// `excludeFieldIds` lets the caller skip fields it fills in itself
// (patient_info/ordering_provider — always server-populated from
// clinician_patient_records + the clinician's own account, never re-typed
// here — see OrderTest.js's own comment for why).
//
// `readOnly` — Lab Orders dashboard addition (admin + staff order-detail
// view): renders this EXACT same form, same section/field layout the
// clinician actually filled in, but every control disabled and `onChange`
// never wired to a real state update ­— a true "digital copy of the web
// form", not a second, parallel display component that could drift out
// of sync with this one over time. `onChange` may be omitted entirely
// when readOnly (nothing ever calls it).
// `errorFieldIds` — new, additive prop (array or Set of field ids,
// defaults to none so every existing caller that doesn't pass it renders
// exactly as before). Drives real required-field validation feedback
// (red label + "This field is required." + red border on text/date/
// number fields) — see isFieldMissing/validateSchemaRequired above, which
// the caller (OrderTest.js) uses to compute this set before passing it in.
// 2026-10-14 fix — real gap, reported with evidence: a field like
// "opioid_risk_codes" (ICD-10 diagnosis codes for a given profile) stayed
// fully editable even when its sibling "opioid_risk_selected" checkbox was
// unchecked — there was no mechanism anywhere linking a field to a
// checkbox that should gate it. A schema field can now set
// `depends_on_checked: "<other field id>"`, meaning: only editable while
// that other field's checkbox_group value contains "selected". Generic —
// not special-cased to ICD/Proove — any future schema can use the same
// key for the same "field only matters if this box is checked" shape.
function _dependencyChecked(data, fieldId) {
  const v = data[fieldId];
  return Array.isArray(v) && v.includes("selected");
}

function _allFields(sections) {
  return sections.flatMap((s) => s.fields);
}

export default function DynamicRequisitionForm({ schema, excludeSectionIds = [], data, onChange, isDark, readOnly = false, errorFieldIds }) {
  const sections = (schema || []).filter((s) => !excludeSectionIds.includes(s.id));
  const errorSet = errorFieldIds instanceof Set ? errorFieldIds : new Set(errorFieldIds || []);

  const setField = (fieldId, value) => {
    if (readOnly) return;
    const next = { ...data, [fieldId]: value };
    // If this change just turned OFF a checkbox that gates other fields,
    // clear those dependent fields too — an unchecked test's ICD codes
    // must not silently ride along in the submitted order.
    for (const f of _allFields(sections)) {
      if (f.depends_on_checked === fieldId && !_dependencyChecked(next, fieldId)) {
        next[f.id] = [];
      }
    }
    onChange(next);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {sections.map((section) => (
        <Box key={section.id}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: isDark ? BRAND.gold : BRAND.navy, mb: 1.5, letterSpacing: "0.03em" }}>
            {section.title}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {section.fields.map((field) => {
              const missing = errorSet.has(field.id);
              const gated = field.depends_on_checked && !_dependencyChecked(data, field.depends_on_checked);
              return (
                <Box key={field.id} sx={gated ? { opacity: 0.5 } : undefined}>
                  <FieldLabel label={field.label} help={field.help} required={field.required} isDark={isDark} missing={missing} />
                  <RequisitionField field={field} value={data[field.id]} onChange={(v) => setField(field.id, v)} isDark={isDark} readOnly={readOnly || gated} missing={missing} />
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
